import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  Paper,
  Grid,
  Stack,
  Chip,
  Card,
  CardActionArea,
} from '@mui/material';
import {
  Home as HomeIcon,
  BusinessCenter as BusinessCenterIcon,
  HeadsetMic as HeadsetMicIcon,
  CalculateOutlined as CalculateIcon,
  ReceiptLongOutlined as ReceiptIcon,
  CorporateFareOutlined as CorporateIcon,
  ArrowForward as ArrowForwardIcon,
  SearchOffOutlined as SearchOffIcon,
} from '@mui/icons-material';
import SEOHead from '../components/SEOHead';

const quickDestinations = [
  {
    title: 'Tax Planning & ITR Filing',
    description: 'Expert income tax calculation, optimization, and e-filing advisory.',
    path: '/tax-planning',
    icon: <CalculateIcon sx={{ fontSize: 28, color: '#C59B27' }} />,
  },
  {
    title: 'GST Advisory & Returns',
    description: 'GST registration, monthly reconciliation, audits, and notice handling.',
    path: '/gst-services',
    icon: <ReceiptIcon sx={{ fontSize: 28, color: '#C59B27' }} />,
  },
  {
    title: 'MCA & Company Compliance',
    description: 'ROC filings, company incorporation, and corporate governance.',
    path: '/mca-compliance',
    icon: <CorporateIcon sx={{ fontSize: 28, color: '#C59B27' }} />,
  },
];

const NotFound: React.FC = () => {
  return (
    <>
      <SEOHead
        title="Page Not Found | Fingard Partners"
        description="The page you are looking for could not be found."
        keywords="404, page not found, Fingard Partners, Chartered Accountants, financial advisory"
      />

      <Box
        sx={{
          minHeight: '80vh',
          py: { xs: 6, md: 10 },
          background: 'linear-gradient(180deg, #F8FAFC 0%, #EDF2F7 100%)',
          display: 'flex',
          alignItems: 'center',
          fontFamily: '"Poppins", sans-serif',
        }}
      >
        <Container maxWidth="md">
          <Paper
            elevation={0}
            sx={{
              p: { xs: 4, md: 6 },
              borderRadius: 4,
              border: '1px solid #E2E8F0',
              background: '#FFFFFF',
              boxShadow: '0 20px 40px -15px rgba(11, 40, 71, 0.08)',
              textAlign: 'center',
              position: 'relative',
              overflow: 'hidden',
            }}
          >
            {/* Top decorative accent bar */}
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: 5,
                background: 'linear-gradient(90deg, #0B2847 0%, #123D6B 50%, #C59B27 100%)',
              }}
            />

            {/* Error badge */}
            <Box sx={{ mb: 2 }}>
              <Chip
                icon={<SearchOffIcon sx={{ '&&': { color: '#C59B27', fontSize: 18 } }} />}
                label="Error 404 • Resource Unavailable"
                sx={{
                  backgroundColor: 'rgba(197, 155, 39, 0.12)',
                  color: '#0B2847',
                  fontWeight: 600,
                  fontSize: '0.85rem',
                  letterSpacing: '0.04em',
                  px: 1.5,
                  py: 0.5,
                  borderRadius: 3,
                  border: '1px solid rgba(197, 155, 39, 0.3)',
                  fontFamily: '"Poppins", sans-serif',
                }}
              />
            </Box>

            {/* Large Branded 404 Headline */}
            <Typography
              variant="h1"
              sx={{
                fontSize: { xs: '5.5rem', sm: '7.5rem', md: '9rem' },
                fontWeight: 900,
                lineHeight: 1,
                letterSpacing: '-0.03em',
                fontFamily: '"Poppins", sans-serif',
                background: 'linear-gradient(135deg, #0B2847 25%, #123D6B 65%, #C59B27 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                mb: 1,
              }}
            >
              404
            </Typography>

            <Typography
              variant="h4"
              sx={{
                color: '#0B2847',
                fontWeight: 700,
                fontFamily: '"Poppins", sans-serif',
                fontSize: { xs: '1.5rem', sm: '2rem' },
                mb: 1.5,
              }}
            >
              Page Not Found
            </Typography>

            <Typography
              variant="body1"
              sx={{
                color: '#486581',
                fontSize: { xs: '1rem', sm: '1.125rem' },
                maxWidth: 580,
                mx: 'auto',
                mb: 4,
                lineHeight: 1.6,
                fontFamily: '"Poppins", sans-serif',
              }}
            >
              The page you're looking for doesn't exist or has been moved.
            </Typography>

            {/* Three Primary CTA Buttons */}
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={2}
              justifyContent="center"
              alignItems="center"
              sx={{ mb: 6 }}
            >
              <Button
                component={RouterLink}
                to="/"
                variant="contained"
                size="large"
                startIcon={<HomeIcon />}
                sx={{
                  backgroundColor: '#123D6B',
                  color: '#FFFFFF',
                  fontWeight: 600,
                  fontFamily: '"Poppins", sans-serif',
                  px: 3.5,
                  py: 1.4,
                  borderRadius: 2.5,
                  minWidth: { xs: '100%', sm: 160 },
                  boxShadow: '0 8px 20px rgba(18, 61, 107, 0.25)',
                  '&:hover': {
                    backgroundColor: '#0B2847',
                    boxShadow: '0 10px 24px rgba(11, 40, 71, 0.35)',
                  },
                }}
              >
                Go Home
              </Button>

              <Button
                component={RouterLink}
                to="/services"
                variant="outlined"
                size="large"
                startIcon={<BusinessCenterIcon />}
                sx={{
                  color: '#123D6B',
                  borderColor: '#123D6B',
                  borderWidth: 2,
                  fontWeight: 600,
                  fontFamily: '"Poppins", sans-serif',
                  px: 3.5,
                  py: 1.3,
                  borderRadius: 2.5,
                  minWidth: { xs: '100%', sm: 160 },
                  '&:hover': {
                    borderWidth: 2,
                    borderColor: '#0B2847',
                    backgroundColor: 'rgba(18, 61, 107, 0.04)',
                  },
                }}
              >
                Our Services
              </Button>

              <Button
                component={RouterLink}
                to="/contact"
                variant="outlined"
                size="large"
                startIcon={<HeadsetMicIcon />}
                sx={{
                  color: '#C59B27',
                  borderColor: '#C59B27',
                  borderWidth: 2,
                  fontWeight: 600,
                  fontFamily: '"Poppins", sans-serif',
                  px: 3.5,
                  py: 1.3,
                  borderRadius: 2.5,
                  minWidth: { xs: '100%', sm: 160 },
                  '&:hover': {
                    borderWidth: 2,
                    borderColor: '#A38017',
                    backgroundColor: 'rgba(197, 155, 39, 0.06)',
                  },
                }}
              >
                Contact Us
              </Button>
            </Stack>

            {/* Quick Navigation Box */}
            <Box
              sx={{
                pt: 4,
                borderTop: '1px solid #E2E8F0',
                textAlign: 'left',
              }}
            >
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 700,
                  color: '#0B2847',
                  textTransform: 'uppercase',
                  letterSpacing: '0.06em',
                  fontSize: '0.78rem',
                  mb: 2.5,
                  textAlign: { xs: 'center', sm: 'left' },
                  fontFamily: '"Poppins", sans-serif',
                }}
              >
                Popular Destinations
              </Typography>

              <Grid container spacing={2}>
                {quickDestinations.map((item) => (
                  <Grid item xs={12} sm={4} key={item.title}>
                    <Card
                      elevation={0}
                      sx={{
                        height: '100%',
                        borderRadius: 2.5,
                        border: '1px solid #E2E8F0',
                        backgroundColor: '#F8FAFC',
                        transition: 'all 0.25s ease',
                        '&:hover': {
                          borderColor: '#C59B27',
                          transform: 'translateY(-3px)',
                          boxShadow: '0 8px 16px rgba(11, 40, 71, 0.06)',
                        },
                      }}
                    >
                      <CardActionArea
                        component={RouterLink}
                        to={item.path}
                        sx={{ p: 2, height: '100%' }}
                      >
                        <Box sx={{ mb: 1 }}>{item.icon}</Box>
                        <Typography
                          variant="subtitle1"
                          sx={{
                            fontWeight: 700,
                            color: '#0B2847',
                            fontSize: '0.95rem',
                            mb: 0.5,
                            fontFamily: '"Poppins", sans-serif',
                          }}
                        >
                          {item.title}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            color: '#486581',
                            fontSize: '0.8rem',
                            lineHeight: 1.4,
                            fontFamily: '"Poppins", sans-serif',
                          }}
                        >
                          {item.description}
                        </Typography>
                      </CardActionArea>
                    </Card>
                  </Grid>
                ))}
              </Grid>

              {/* Need assistance helpline */}
              <Box
                sx={{
                  mt: 4,
                  p: 2,
                  borderRadius: 2,
                  backgroundColor: 'rgba(18, 61, 107, 0.04)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  flexWrap: 'wrap',
                  gap: 1.5,
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    color: '#102A43',
                    fontWeight: 500,
                    fontFamily: '"Poppins", sans-serif',
                  }}
                >
                  Need direct assistance with your tax or corporate compliance matters?
                </Typography>
                <Button
                  component={RouterLink}
                  to="/contact"
                  endIcon={<ArrowForwardIcon sx={{ fontSize: 16 }} />}
                  sx={{
                    color: '#123D6B',
                    fontWeight: 700,
                    fontSize: '0.85rem',
                    textTransform: 'none',
                    p: 0,
                    fontFamily: '"Poppins", sans-serif',
                    '&:hover': {
                      backgroundColor: 'transparent',
                      color: '#C59B27',
                    },
                  }}
                >
                  Speak with a CA
                </Button>
              </Box>
            </Box>
          </Paper>
        </Container>
      </Box>
    </>
  );
};

export default NotFound;
